/**
 * The one WebSocket client every view uses, through `ViewContext`.
 *
 * Responsibilities: connect, `hello`, attach or join, keep-alive pings,
 * reconnect with backoff (re-attaching or re-joining automatically), and
 * request/response matching by `ref` so `send(intent)` returns a promise
 * that rejects with the server's German reason.
 *
 * Views never touch this class directly; the hub creates one per page and
 * hands views a `ViewContext` built from it.
 */
import {
  CONNECTION_MESSAGES,
  ERROR_CODES,
  GAME_MESSAGES,
  PROTOCOL_VERSION,
  SESSION_MESSAGES,
  type Envelope,
  type ErrorPayload,
  type HelloResponse,
  type JoinedPayload,
  type SessionStatePayload,
} from '@schulspiele/protocol';

export type Identity =
  | { kind: 'teacher'; sessionId: string }
  | { kind: 'board'; sessionId: string; token: string }
  | { kind: 'student'; pin: string; name: string }
  | { kind: 'student-reconnect'; reconnectToken: string };

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface SessionClientEvents {
  status: (status: ConnectionStatus) => void;
  state: (state: SessionStatePayload) => void;
  view: (view: unknown) => void;
  event: (event: Envelope) => void;
  joined: (joined: JoinedPayload & { sessionId: string }) => void;
  kicked: (reason: string) => void;
  /** A protocol-level error not tied to a pending request. */
  error: (error: ErrorPayload) => void;
  /**
   * The server refused the attach or join (wrong PIN, locked, name taken,
   * seat no longer valid). The client has stopped; retrying would not help.
   * Not emitted for network trouble: then it simply reconnects.
   */
  refused: (error: ErrorPayload) => void;
  /** Another tab or device took over this seat (close code 4001). The client has stopped. */
  replaced: () => void;
}

export class SessionClientError extends Error {
  public constructor(
    public readonly code: ErrorPayload['code'],
    reason?: string,
    /** True for network trouble (dropped, timed out), false for an answer from the server. */
    public readonly transport = false,
  ) {
    super(reason ?? code);
    this.name = 'SessionClientError';
  }
}

const PING_INTERVAL_MS = 5000;

export class SessionClient {
  public status: ConnectionStatus = 'closed';
  public sessionId: string | null = null;
  public clockOffset = 0;
  public lastState: SessionStatePayload | null = null;
  public lastView: unknown = null;

  private ws: WebSocket | null = null;
  private identity: Identity;
  private refCounter = 0;
  private readonly pending = new Map<
    string,
    { resolve: (e: Envelope) => void; reject: (e: SessionClientError) => void }
  >();
  private readonly listeners: { [K in keyof SessionClientEvents]: SessionClientEvents[K][] } = {
    status: [],
    state: [],
    view: [],
    event: [],
    joined: [],
    kicked: [],
    error: [],
    refused: [],
    replaced: [],
  };
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectDelay = 1000;
  private stopped = false;
  /** When the server last sent anything (a pong counts). */
  private lastMessageAt = 0;
  private wakeListening = false;

  public constructor(identity: Identity) {
    this.identity = identity;
  }

  /**
   * Phones come back from a locked screen, another app or a Wi-Fi change
   * with a socket that may be dead without knowing it. On wake-up: if the
   * socket is closed, reconnect now instead of waiting for the backoff; if
   * it looks open, ping and give the server 4 s to answer, else start over.
   */
  private readonly onWake = (): void => {
    if (this.stopped || document.visibilityState === 'hidden') return;
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.probe(4000);
    } else if (this.status === 'reconnecting') {
      this.reconnectNow();
    }
  };

  private probe(timeoutMs: number): void {
    const ws = this.ws;
    if (ws === null || this.sessionId === null) return;
    const sentAt = Date.now();
    ws.send(JSON.stringify({ type: SESSION_MESSAGES.ping, sessionId: this.sessionId }));
    window.setTimeout(() => {
      if (this.ws === ws && !this.stopped && this.lastMessageAt < sentAt) this.reconnectNow();
    }, timeoutMs);
  }

  /** Drop the current socket (even a half-open one) and connect again at once. */
  private reconnectNow(): void {
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const old = this.ws;
    this.ws = null;
    this.stopPing();
    for (const p of this.pending.values())
      p.reject(new SessionClientError('rejected', 'Verbindung getrennt.', true));
    this.pending.clear();
    old?.close();
    this.reconnectDelay = 1000;
    this.setStatus('reconnecting');
    this.connect();
  }

  public on<K extends keyof SessionClientEvents>(
    event: K,
    handler: SessionClientEvents[K],
  ): () => void {
    const list = this.listeners[event] as SessionClientEvents[K][];
    list.push(handler);
    return () => {
      const i = list.indexOf(handler);
      if (i >= 0) list.splice(i, 1);
    };
  }

  private emit<K extends keyof SessionClientEvents>(
    event: K,
    ...args: Parameters<SessionClientEvents[K]>
  ): void {
    for (const h of this.listeners[event] as ((...a: Parameters<SessionClientEvents[K]>) => void)[])
      h(...args);
  }

  public connect(): void {
    this.stopped = false;
    if (!this.wakeListening) {
      this.wakeListening = true;
      document.addEventListener('visibilitychange', this.onWake);
      window.addEventListener('online', this.onWake);
      window.addEventListener('pageshow', this.onWake);
    }
    this.setStatus(this.status === 'closed' ? 'connecting' : 'reconnecting');
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${scheme}://${location.host}/ws`);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.lastMessageAt = Date.now();
      void this.handshake();
    });
    ws.addEventListener('message', (e) => {
      if (ws !== this.ws) return;
      this.lastMessageAt = Date.now();
      this.onMessage(JSON.parse(String(e.data)) as Envelope);
    });
    ws.addEventListener('close', (e) => {
      // A socket we already replaced (reconnectNow) closes late; ignore it.
      if (ws !== this.ws) return;
      this.stopPing();
      for (const p of this.pending.values())
        p.reject(new SessionClientError('rejected', 'Verbindung getrennt.', true));
      this.pending.clear();
      // 4000 kicked, 4002 session ended, 4001 replaced: no reconnect.
      if (this.stopped || e.code === 4000 || e.code === 4001 || e.code === 4002) {
        this.setStatus('closed');
        if (e.code === 4001 && !this.stopped) this.emit('replaced');
        return;
      }
      this.setStatus('reconnecting');
      this.reconnectTimer = window.setTimeout(() => {
        this.connect();
      }, this.reconnectDelay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 15000);
    });
  }

  public close(): void {
    this.stopped = true;
    if (this.wakeListening) {
      this.wakeListening = false;
      document.removeEventListener('visibilitychange', this.onWake);
      window.removeEventListener('online', this.onWake);
      window.removeEventListener('pageshow', this.onWake);
    }
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.stopPing();
    this.ws?.close();
    this.setStatus('closed');
  }

  /**
   * Send an envelope and wait for the server's reply to it: `ack` for
   * attach, intents and commands; `hello`, `session/joined` or
   * `session/pong` for those; `error` on refusal. Every reply carries the
   * request's `ref`. A request that gets no reply within 10 s rejects.
   */
  public request(envelope: Envelope): Promise<Envelope> {
    const ws = this.ws;
    if (ws?.readyState !== WebSocket.OPEN) {
      return Promise.reject(new SessionClientError('rejected', 'Keine Verbindung.', true));
    }
    this.refCounter += 1;
    const ref = `r${String(this.refCounter)}`;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        if (this.pending.delete(ref))
          reject(new SessionClientError('rejected', 'Keine Antwort vom Server.', true));
      }, 10_000);
      this.pending.set(ref, {
        resolve: (e) => {
          window.clearTimeout(timer);
          resolve(e);
        },
        reject: (e) => {
          window.clearTimeout(timer);
          reject(e);
        },
      });
      ws.send(JSON.stringify({ ...envelope, ref }));
    });
  }

  /**
   * A child gives up its seat: tell the server (the name is free at once),
   * then close. Without a connection the seat stays until the game ends or
   * the teacher removes it (seats are kept, U16).
   */
  public async leave(): Promise<void> {
    if (this.sessionId !== null) {
      try {
        await this.request({ type: SESSION_MESSAGES.leave, sessionId: this.sessionId });
      } catch {
        // The server may close first; the seat is gone either way.
      }
    }
    this.close();
  }

  /** Send a module intent. Rejects with the module's German reason. */
  public sendIntent(intent: { type: string; payload?: unknown }): Promise<void> {
    if (this.sessionId === null)
      return Promise.reject(new SessionClientError('rejected', 'Nicht verbunden.', true));
    return this.request({
      type: GAME_MESSAGES.intent,
      sessionId: this.sessionId,
      payload: intent,
    }).then(() => undefined);
  }

  /** Teacher commands. */
  public command(type: string, payload?: unknown): Promise<void> {
    if (this.sessionId === null)
      return Promise.reject(new SessionClientError('rejected', 'Nicht verbunden.', true));
    return this.request({
      type,
      sessionId: this.sessionId,
      ...(payload === undefined ? {} : { payload }),
    }).then(() => undefined);
  }

  /* ---------------------------------------------------------------- */

  private async handshake(): Promise<void> {
    // The socket this handshake belongs to; reconnectNow may replace it meanwhile.
    const ws = this.ws;
    try {
      const hello = await this.request({
        type: CONNECTION_MESSAGES.hello,
        payload: { protocolVersion: PROTOCOL_VERSION },
      });
      const payload = hello.payload as HelloResponse;
      this.clockOffset = payload.serverTime - Date.now();

      const id = this.identity;
      if (id.kind === 'teacher') {
        await this.request({
          type: SESSION_MESSAGES.attach,
          sessionId: id.sessionId,
          payload: { role: 'teacher' },
        });
        this.sessionId = id.sessionId;
      } else if (id.kind === 'board') {
        await this.request({
          type: SESSION_MESSAGES.attach,
          sessionId: id.sessionId,
          payload: { role: 'board', token: id.token },
        });
        this.sessionId = id.sessionId;
      } else if (id.kind === 'student') {
        const joined = await this.request({
          type: SESSION_MESSAGES.join,
          payload: { pin: id.pin, name: id.name },
        });
        this.onJoined(joined);
      } else {
        const joined = await this.request({
          type: SESSION_MESSAGES.join,
          payload: { reconnectToken: id.reconnectToken },
        });
        this.onJoined(joined);
      }
      this.reconnectDelay = 1000;
      this.setStatus('open');
      this.startPing();
    } catch (err) {
      if (ws !== this.ws) return; // a newer socket has its own handshake
      const error =
        err instanceof SessionClientError
          ? err
          : new SessionClientError('rejected', String(err), true);
      if (error.transport) {
        // Network trouble mid-handshake: drop this socket, the close handler reconnects.
        ws?.close();
        return;
      }
      // The server said no. Retrying blindly would not change its mind.
      this.stopped = true;
      ws?.close();
      this.emit('refused', { code: error.code, reason: error.message });
      this.emit('error', { code: error.code, reason: error.message });
    }
  }

  private onJoined(envelope: Envelope): void {
    const payload = envelope.payload as JoinedPayload;
    const sessionId = envelope.sessionId ?? '';
    this.sessionId = sessionId;
    if (payload.reconnectToken !== undefined) {
      // From now on reconnect with the token, never with pin and name again.
      this.identity = { kind: 'student-reconnect', reconnectToken: payload.reconnectToken };
    }
    this.emit('joined', { ...payload, sessionId });
  }

  private onMessage(envelope: Envelope): void {
    if (envelope.ref !== undefined) {
      const p = this.pending.get(envelope.ref);
      if (p !== undefined) {
        this.pending.delete(envelope.ref);
        if (envelope.type === CONNECTION_MESSAGES.error) {
          const e = envelope.payload as ErrorPayload;
          p.reject(new SessionClientError(e.code, e.reason));
        } else {
          p.resolve(envelope);
        }
        // `joined` is also a state change the page listens to; everything
        // else that answers a ref is fully handled by the promise.
        if (envelope.type !== SESSION_MESSAGES.joined) return;
      }
    }
    switch (envelope.type) {
      case SESSION_MESSAGES.state:
        this.lastState = envelope.payload as SessionStatePayload;
        this.emit('state', this.lastState);
        return;
      case GAME_MESSAGES.view:
        this.lastView = (envelope.payload as { view: unknown }).view;
        this.emit('view', this.lastView);
        return;
      case GAME_MESSAGES.event:
        this.emit('event', envelope);
        return;
      case SESSION_MESSAGES.kicked:
        this.stopped = true;
        this.emit('kicked', (envelope.payload as { reason: string }).reason);
        return;
      case SESSION_MESSAGES.pong:
      case CONNECTION_MESSAGES.ack:
        return;
      case CONNECTION_MESSAGES.error: {
        const e = envelope.payload as ErrorPayload;
        if (e.code === ERROR_CODES.versionMismatch) location.reload();
        this.emit('error', e);
        return;
      }
      default:
        return;
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN && this.sessionId !== null) {
        // Three pings without any answer: the socket is dead even if it looks open.
        if (Date.now() - this.lastMessageAt > PING_INTERVAL_MS * 3) {
          this.reconnectNow();
          return;
        }
        this.ws.send(JSON.stringify({ type: SESSION_MESSAGES.ping, sessionId: this.sessionId }));
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit('status', status);
  }
}
